import { z } from "zod";
import { DENSITY_CHART_BUCKET_UNITS } from "@design-system/DensityChart/densityBuckets.lib";

/** What one bar of the Activity chart covers, as the density setting stores it. */
export const activityChartDensitySchema = z.enum(DENSITY_CHART_BUCKET_UNITS);

export type ActivityChartDensity = z.infer<typeof activityChartDensitySchema>;

export const DENSITY_LABELS: Record<ActivityChartDensity, string> = {
    day: "Day",
    week: "Week",
    month: "Month",
};

/** The density the chart showed for `closest`, the one it fitted the window to. */
export interface ShownActivityChartDensity {
    closest: ActivityChartDensity;
    shown: ActivityChartDensity;
}

export interface ActivityChartDensityView {
    /** The finest density the chart fits the window to. */
    closest: ActivityChartDensity;
    /** What the chart shows: `closest`, or a coarser one while the window doesn't fit it. */
    shown: ActivityChartDensity;
    /** What the picker selects: the chosen density, else the shown one. */
    selected: ActivityChartDensity;
    /** The line under the picker while the chart shows a coarser density than the chosen one. */
    hint: string | undefined;
}

/**
 * What the chart and its picker show for the chosen density, picked or
 * saved. With none chosen, the chart fits the window from days up, and the
 * picker follows it without a hint. A report from the chart counts only
 * for the current `closest`.
 */
export function toDensityView({
    chosen,
    reported,
}: {
    chosen: ActivityChartDensity | undefined;
    reported?: ShownActivityChartDensity;
}): ActivityChartDensityView {
    const closest = chosen ?? "day";
    const shown = reported?.closest === closest ? reported.shown : closest;
    return {
        closest,
        shown,
        selected: chosen ?? shown,
        hint:
            chosen === undefined || shown === chosen
                ? undefined
                : `Showing ${DENSITY_LABELS[shown]} to fit your range`,
    };
}
